import { useApolloClient } from '@apollo/client';
import { Loader } from '@opendoor/bricks/core';
import { useObservability } from 'shared/helpers/observability';
import React, { useMemo, useState } from 'react';
import { VirtualAssessment_sellDirect_getLead_lead_latestSelfServeVirtualAssessment_ssvaRecordings as Recording } from 'sellDirect/graphql/__generated__/VirtualAssessment';
import { FileUploadCard } from 'sellDirect/components/FileUploadCard';
import { useQualificationContext } from 'shared/helpers/QualificationContext';
import {
  FileUpload,
  generateS3Key,
  S3UploadDestination,
  uploadFileToS3,
} from 'shared/helpers/s3Helpers';
import {
  OdProtosWebData_RecordedFeature as RecordedFeature,
  OdProtosWebData_VirtualAssessmentRecordingInput,
} from '__generated__/globalTypes';
import { TaskCtas } from 'sellDirect/components/ssva/TaskCtas';
import { useUpdateVirtualAssessmentRecordings } from 'sellDirect/graphql/useUpdateVirtualAssessmentRecordings';
import { useVirtualAssessmentData } from 'sellDirect/graphql/useVirtualAssessmentData';

interface FileInteractionProps {
  recordedFeature: RecordedFeature;
  submissionCompleted: boolean;
  ssvaRecordings: Recording[];
}

/**
 * Renders UI for uploading files and save or submit task.
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const FileInteraction: React.FC<FileInteractionProps> = ({
  recordedFeature,
  submissionCompleted,
  ssvaRecordings,
}) => {
  const { updateSSVARecordings, clearLastSSVARecordingByFeature } =
    useUpdateVirtualAssessmentRecordings();
  const { refetch } = useVirtualAssessmentData();
  const { sellerInputUUID } = useQualificationContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const apolloClient = useApolloClient();
  const { trackEvent } = useObservability();

  const filesUploaded = useMemo(() => {
    const recordings: Array<Recording> =
      ssvaRecordings.filter((recording) => recording.recordedFeature === recordedFeature) ?? [];
    return recordings.map((recording: Recording) => {
      return {
        file: new File([], recording.fileName),
        s3Key: '',
        progress: 100,
        uploadLatency: 0,
        completed: true,
        errorOccurred: false,
        errorMessage: '',
        downloadUrl: recording.preSignedRecordingUrl,
      };
    });
  }, [recordedFeature, ssvaRecordings]);

  const updateRecordings = async (uploads: Array<FileUpload>) => {
    const newRecordings: Array<OdProtosWebData_VirtualAssessmentRecordingInput> =
      uploads.map((upload) => {
        return {
          fileName: upload.file.name,
          recordedFeature,
          s3Directory: upload.s3Key,
        };
      }) ?? [];

    trackEvent('self-schedule', 'uploaded-new-recording', { recordedFeature });
    await updateSSVARecordings(newRecordings);
  };

  const uploadFile = (file: FileUpload, onUpdate: (file: FileUpload) => void) => {
    uploadFileToS3(file, apolloClient, false, onUpdate);
  };

  const getS3Key = (file: File): string => {
    return generateS3Key(file, sellerInputUUID, S3UploadDestination.SSVA);
  };

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <FileUploadCard
          uploadedFiles={[...filesUploaded]}
          buttonText="Upload"
          modalDisplayTitle="Seller's disclosure"
          getS3Key={getS3Key}
          uploadFile={uploadFile}
          variant="primary"
          size="md"
          width="100%"
          accept="application/pdf"
          multiple={false}
          onAllUploadComplete={async (files: Array<FileUpload>) => {
            setIsLoading(true);
            await updateRecordings(files);
            await refetch();
            setIsLoading(false);
          }}
          deleteFile={
            submissionCompleted
              ? undefined
              : async () => {
                  setIsLoading(true);
                  await clearLastSSVARecordingByFeature(recordedFeature);
                  await refetch();
                  setIsLoading(false);
                }
          }
          renderCtaWithoutCardContainer
        />
      )}
      {filesUploaded.length > 0 && (
        <TaskCtas
          recordedFeature={recordedFeature}
          loading={isLoading}
          submissionCompleted={submissionCompleted}
        />
      )}
    </>
  );
};
